import type { CreditLineCheckoutStatus, IAccountCreditLine } from '@share/common-types';
import { roundMoney2 } from './currency';

const LOW_USAGE_RATIO = 0.8;

export type CreditLineChargeInput = {
  payOrBalance: number;
  clientCashPoints?: number | null;
  conversionRate?: number;
  insuranceAmount?: number;
  rewardDeduction?: number;
};

/** Net amount charged to credit line (matches checkout express-charge totals). */
export const computeCreditLineChargeTotal = ({
  payOrBalance,
  clientCashPoints = 0,
  conversionRate = 1,
  insuranceAmount = 0,
  rewardDeduction = 0,
}: CreditLineChargeInput): number => {
  const finalBalance = roundMoney2(payOrBalance - (clientCashPoints ?? 0) * conversionRate);
  return Math.max(0, finalBalance + insuranceAmount - rewardDeduction);
};
const LOW_REMAINING_RATIO = 0.1;

export type CreditLineCheckoutSummary = {
  available: number;
  bookingAmount: number;
  remaining: number;
  status: CreditLineCheckoutStatus;
  insufficient: boolean;
  lowUsage: boolean;
  showLowRemainingWarning: boolean;
};

export const getCreditLineCheckoutSummary = (
  creditLine: IAccountCreditLine | null | undefined,
  bookingAmount: number,
): CreditLineCheckoutSummary | null => {
  if (creditLine == null || !Number.isFinite(creditLine.currentAmount)) {
    return null;
  }

  const available = creditLine.currentAmount;
  const booking = Math.max(0, bookingAmount);
  const remaining = available - booking;

  const insufficient = remaining < 0;
  const lowUsage = !insufficient && available > 0 && booking / available > LOW_USAGE_RATIO;
  const showLowRemainingWarning =
    !insufficient && available > 0 && remaining / available < LOW_REMAINING_RATIO;

  let status: CreditLineCheckoutStatus = 'sufficient';
  if (insufficient) {
    status = 'insufficient';
  } else if (lowUsage) {
    status = 'low';
  }

  return {
    available,
    bookingAmount: booking,
    remaining,
    status,
    insufficient,
    lowUsage,
    showLowRemainingWarning,
  };
};

